import { configureStore, Middleware, isRejectedWithValue } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { apiSlice } from '../api/apiSlice';
import { authReducer } from './slices/authSlice';
import { uiReducer } from './slices/uiSlice';
import { borefieldReducer } from '../features/borefields/store/borefieldSlice';
import { logger } from '../lib/logger';
import { storage } from '../lib/storage';

// Error logging middleware
const errorMiddleware: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    logger.error('API Error', {
      type: action.type,
      payload: action.payload,
      meta: action.meta,
    });
  }
  return next(action);
};

// Local storage persistence middleware
const LOCAL_STORAGE_KEY = 'geothermal-app-state';

const loadPersistedState = () => {
  try {
    const serialized = storage.getItem(LOCAL_STORAGE_KEY);
    if (serialized === null) return undefined;
    const parsed = JSON.parse(serialized) as { ui?: unknown };
    return {
      ui: parsed.ui,
    };
  } catch {
    return undefined;
  }
};

const persistMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);
  try {
    const state = store.getState() as RootState;
    const toPersist = {
      ui: {
        unitSystem: state.ui.unitSystem,
        sidebarCollapsed: state.ui.sidebarCollapsed,
      },
    };
    storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // Ignore storage errors
  }
  return result;
};

export const store = configureStore({
  reducer: {
    [apiSlice.reducerPath]: apiSlice.reducer,
    auth: authReducer,
    ui: uiReducer,
    borefield: borefieldReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(apiSlice.middleware)
      .concat(errorMiddleware)
      .concat(persistMiddleware),
  preloadedState: loadPersistedState(),
});

// Enable refetch on focus/reconnect for RTK Query
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
